import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FusaOrganization } from "@/backend/models/fusaOrganization";
import { RequirementSubjectType } from "@/backend/access/requirement/types";
import { useFusaOrganizationStore } from "@/frontend/store/FusaOrganizationStore";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { NotFound } from "@/frontend/pages/NotFound";
import { I18n } from "@/i18n/I18n";
import { CardPlaceholder } from "../cards/CardPlaceholder";
import { ProjectTemplates } from "../projects/ProjectTemplates";
import { OrganizationRequirements } from "../requirements/OrganizationRequirements";
import { Select } from "../Select";

/** The Library's sub-pages, by the path segment after `/library/`. */
export const LIBRARY_SECTIONS = [
  { key: 'project-templates', label: 'projectTemplates' },
  { key: 'requirements', label: 'requirements' },
] as const;

type LibrarySection = typeof LIBRARY_SECTIONS[number]['key'];

/**
 * Org-level content -- project templates and the requirement library -- that
 * used to live as tabs on the organization page. An enterprise can have several
 * organizations, so the one shown is picked with `?org=`, defaulting to the first.
 */
export function LibraryPage() {
  const section = useParams().section as LibrarySection | undefined;
  const [searchParams] = useSearchParams();
  const fusaOrganizations = useFusaOrganizationStore(state => state.fusaOrganizations);

  if (!LIBRARY_SECTIONS.some(s => s.key === section)) {
    return <NotFound />;
  }

  const orgId = searchParams.get('org');
  const organization = fusaOrganizations.find(o => o.id === orgId) ?? fusaOrganizations[0];

  return (
    <div className="p-3 overflow-auto" style={{ maxHeight: '100vh', height: '100vh' }}>
      <div className="pb-3 d-flex align-items-center justify-content-between gap-3">
        <h1 className="mb-0">{I18n.get('library')}</h1>
        {fusaOrganizations.length > 1 && organization && (
          <OrganizationPicker section={section!} organizations={fusaOrganizations} selected={organization} />
        )}
      </div>

      <div className="veriform-tab-bar mb-3">
        {LIBRARY_SECTIONS.map(s => (
          <Link
            key={s.key}
            to={{ pathname: `/library/${s.key}`, search: orgId ? `?org=${orgId}` : '' }}
            className={`veriform-tab text-decoration-none ${s.key === section ? 'active' : ''}`}
            aria-current={s.key === section ? 'page' : undefined}
          >
            {I18n.get(s.label)}
          </Link>
        ))}
      </div>

      {!organization ? (
        <p className="text-muted">{I18n.get('noOrganizations')}</p>
      ) : section === 'project-templates' ? (
        <ProjectTemplates key={organization.id} organization={organization} />
      ) : (
        <LibraryRequirements key={organization.id} fusaOrganizationId={organization.id} />
      )}
    </div>
  );
}

function OrganizationPicker({ section, organizations, selected }: { section: LibrarySection; organizations: FusaOrganization[]; selected: FusaOrganization }) {
  return (
    <Select value={selected.title} label={I18n.get('selectOrganization')} neutral>
      {organizations.filter(o => o.id !== selected.id).map(o => (
        <Link key={o.id} to={`/library/${section}?org=${o.id}`} className="w-100 dropdown-item">
          {o.title}
        </Link>
      ))}
    </Select>
  );
}

function LibraryRequirements({ fusaOrganizationId }: { fusaOrganizationId: string }) {
  const fetchRequirementsBySubject = useRequirementsStore(state => state.fetchRequirementsBySubject);
  const reset = useRequirementsStore(state => state.reset);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void fetchRequirementsBySubject(fusaOrganizationId, RequirementSubjectType.FUSA_ORGANIZATION).finally(() => {
      setLoading(false);
    });

    return () => {
      reset();
    };
  }, [fusaOrganizationId, fetchRequirementsBySubject, reset]);

  if (loading) {
    return <CardPlaceholder />;
  }

  return <OrganizationRequirements fusaOrganizationId={fusaOrganizationId} />;
}
